import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Stack,
  Alert,
  Typography,
  CircularProgress,
  useTheme
} from '@mui/material';
import { gql } from '@apollo/client';
import { useOrgConfig } from '~/providers/OrgConfigProvider';
import { startCase } from 'lodash';
import { PatientDetailScreenDocument, useCreatePatientCareTeamMutation, useGetProvidersByTypeQuery } from '~/graphql/__generated__/graphql';

interface CareTeamEditDialogProps {
  open: boolean;
  onClose: () => void;
  patientId: number;
  patientName: string;
  existingCareTeam: Array<{
    id: number;
    providerTypeId: number;
    providerId: number;
    provider?: {
      firstName: string;
      lastName: string;
      providerTypeId: number;
    } | null;
  }>;
  editingMember?: {providerTypeId: number, providerId: number} | null;
}

gql`
  mutation CreatePatientCareTeam(
    $patientId: Int!
    $providerId: Int!
    $providerTypeId: Int!
    $allowAutomaticUpdates: Boolean
  ) @permission(requires: useWebApp) {
    createPatientCareTeam(
      patientId: $patientId
      providerId: $providerId
      providerTypeId: $providerTypeId
      allowAutomaticUpdates: $allowAutomaticUpdates
    )
  }
`;

gql`
  query GetProvidersByType($providerTypeId: Int!) @permission(requires: useWebApp) {
    provider(
      where: { 
        providerTypeId: { eq: $providerTypeId }
        active: { eq: true }
      }
      orderBy: { firstName: { direction: asc, priority: 1 } }
    ) {
      id
      firstName
      lastName
      providerTypeId
    }
  }
`;

const CareTeamEditDialog: React.FC<CareTeamEditDialogProps> = ({
  open,
  onClose,
  patientId,
  patientName,
  existingCareTeam,
  editingMember
}) => {
  const { providerTypes } = useOrgConfig();
  const [selectedProviderTypeId, setSelectedProviderTypeId] = useState<number | ''>('');
  const [selectedProviderId, setSelectedProviderId] = useState<number | ''>('');
  const [showReplaceConfirmation, setShowReplaceConfirmation] = useState(false);
  const [existingProvider, setExistingProvider] = useState<string>('');

  const [createCareTeam, { loading: createLoading }] = useCreatePatientCareTeamMutation();

  const { data: providersData, loading: providersLoading, error: providersError } = useGetProvidersByTypeQuery({
    variables: { providerTypeId: selectedProviderTypeId as number },
    skip: !selectedProviderTypeId || typeof selectedProviderTypeId === 'string'
  });

  const availableProviderTypes = Object.values(providerTypes);

  const availableProviders = providersData?.provider || [];

  // When editing, exclude the current member from the "already assigned" list
  const assignedProviderTypeIds = editingMember ? existingCareTeam
    .filter(ct => ct.providerTypeId !== editingMember.providerTypeId)
    .map(ct => ct.providerTypeId) : existingCareTeam.map(ct => ct.providerTypeId);

  const handleProviderTypeChange = (providerTypeId: number) => {
    setSelectedProviderTypeId(providerTypeId);
    setSelectedProviderId('');
  };

  // Pre-populate fields when editing an existing member
  React.useEffect(() => {
    if (open && editingMember) {
      setSelectedProviderTypeId(editingMember.providerTypeId);
      setSelectedProviderId(editingMember.providerId);
    } else if (open && !editingMember) {
      setSelectedProviderTypeId('');
      setSelectedProviderId('');
    }
  }, [open, editingMember]);

  const handleSubmit = async () => {
    if (!selectedProviderTypeId || !selectedProviderId) return;

    const existing = existingCareTeam.find(ct => ct.providerTypeId === selectedProviderTypeId);
    
    if (existing && !editingMember) {
      const providerName = `${existing.provider?.firstName} ${existing.provider?.lastName}`;
      setExistingProvider(providerName);
      setShowReplaceConfirmation(true);
      return;
    }

    await performCreate();
  };

  const performCreate = async () => {
    try {
      await createCareTeam({
        variables: {
          patientId,
          providerId: selectedProviderId as number,
          providerTypeId: selectedProviderTypeId as number,
          allowAutomaticUpdates: false,
        },
        refetchQueries: [
          {
            query: PatientDetailScreenDocument,
            variables: { patientId },
          },
        ],
        awaitRefetchQueries: true,
      });

      // Close modal immediately after successful mutation
      handleClose();
    } catch (error) {
      console.error('Failed to create care team assignment:', error);
    }
  };

  const handleClose = () => {
    setSelectedProviderTypeId('');
    setSelectedProviderId('');
    setShowReplaceConfirmation(false);
    setExistingProvider('');
    onClose();
  };

  const isEditing = Boolean(editingMember);
  const dialogTitle = isEditing ? 'Edit Care Team Member' : 'Add Care Team Member';

  if (showReplaceConfirmation) {
    const selectedProvider = availableProviders.find((p: any) => p.id === selectedProviderId);
    const providerTypeName = providerTypes[selectedProviderTypeId as number]?.name;

    return (
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Replace Care Team Member?</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">
              {patientName} already has <strong>{existingProvider}</strong> assigned as their{' '}
              <strong>{startCase(providerTypeName)}</strong>.
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              Do you want to replace them with{' '}
              <strong>
                {selectedProvider?.firstName} {selectedProvider?.lastName}
              </strong>
              ?
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button 
            onClick={performCreate} 
            variant="contained" 
            color="warning"
            disabled={createLoading}
            startIcon={createLoading ? <CircularProgress size={20} /> : null}
          >
            {createLoading ? 'Replacing...' : 'Replace'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  const theme = useTheme();

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{dialogTitle}</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 2 }}>
          <TextField
            select
            fullWidth
            label="Provider Type"
            value={selectedProviderTypeId}
            onChange={(e) => handleProviderTypeChange(Number(e.target.value))}
            helperText={isEditing ? "Change the provider type for this care team member." : "Select a provider type. Already assigned types will prompt for replacement."}
            disabled={isEditing}
          >
            {availableProviderTypes.map((providerType) => {
              const isAssigned = assignedProviderTypeIds.includes(providerType.id);
              return (
                <MenuItem key={providerType.id} value={providerType.id}>
                  {startCase(providerType.name)}
                  {isAssigned && <span style={{ color: theme.palette.primary.dark, marginLeft: '8px', fontSize: '0.85em' }}>(assigned)</span>}
                </MenuItem>
              );
            })}
          </TextField>

          {selectedProviderTypeId && (
            <TextField
              select
              fullWidth
              label="Provider"
              value={selectedProviderId}
              onChange={(e) => setSelectedProviderId(Number(e.target.value))}
              disabled={providersLoading}
              helperText={
                providersLoading 
                  ? 'Loading providers...' 
                  : providersError 
                    ? 'Error loading providers' 
                    : availableProviders.length === 0 
                      ? 'No providers available for this type' 
                      : ''
              }
            >
              {availableProviders.length === 0 ? (
                <MenuItem disabled value="" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                  No active providers of this type exist
                </MenuItem>
              ) : (
                availableProviders.map((provider: any) => (
                  <MenuItem key={provider.id} value={provider.id}>
                    {provider.firstName} {provider.lastName}
                  </MenuItem>
                ))
              )}
            </TextField>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained"
          disabled={!selectedProviderTypeId || !selectedProviderId || createLoading}
          startIcon={createLoading ? <CircularProgress size={20} /> : null}
        >
          {createLoading ? (isEditing ? 'Updating...' : 'Adding...') : (isEditing ? 'Update Care Team' : 'Add to Care Team')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CareTeamEditDialog;
